import React, {useCallback, useMemo} from "react";
import {DatePicker, DateTimePicker, TimePicker} from "@mui/x-date-pickers";
import {useFieldControl} from "../control";
import {TextFieldProps} from "@mui/material";
import {DateUtils, DateUtilsTypes} from "../../../../@bizkeytech";

export interface FieldDateTimePickerProps {
    allowTime?: boolean;
    allowDate?: boolean;
    autoCommit?: boolean;
}

export type FieldDatePickerValue = string;

const FieldDateTimePicker = (props: FieldDateTimePickerProps) => {
    const fieldControl = useFieldControl<FieldDatePickerValue>();
    const {setValue, setValueFlushSync, saveChanges} = fieldControl;

    const value = useMemo(() => (fieldControl.value ? DateUtils.serverToUTC(fieldControl.value) : null), [fieldControl.value]);

    /**
     * Invokes the [setValue] callback with the new value.
     */
    const onChange = useCallback((value: DateUtilsTypes.Instance | null) => {
        if (value?.isValid() || value === null) {
            setValue(!value ? undefined : DateUtils.UTCToServer(value).format(DateUtils.withoutOffsetFormat))
        }
    }, [setValue])

    /**
     * Invokes the [setValueFlushSync] callback with the new value.
     */
    const onChangeFlushSync = useCallback((value: DateUtilsTypes.Instance | null) => {
        setValueFlushSync(!value ? undefined : DateUtils.UTCToServer(value).format(DateUtils.withoutOffsetFormat))
    }, [setValueFlushSync])

    /**
     * Handles the key up event.
     * - If the user presses enter, the changes are saved immediately.
     */
    const onKeyUp = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        switch (e.key) {
            case "Enter":
                e.target.dispatchEvent(new Event('blur'));
                setTimeout(() => void saveChanges(), 100)
                break;
        }
    }, [saveChanges])

    const textFieldProps = useMemo<TextFieldProps>(() => ({
        fullWidth: true,
        size: 'small',
        variant: 'outlined',
        onBlur: async (e) => {
            onChangeFlushSync(DateUtils.UTC(e.target.value).isValid() ? DateUtils.localToUTC(DateUtils.local(e.target.value).format(DateUtils.withoutOffsetFormat)) : null);
            if (props.autoCommit ?? true) {
                await saveChanges();
            }
        },
        onKeyUp: onKeyUp,
        InputProps: {
            readOnly: fieldControl.readonly,
        },
        helperText: fieldControl.helperText,
        error: fieldControl.erroneous,
        sx: fieldControl.sx,
    }), [fieldControl.erroneous, fieldControl.helperText, fieldControl.readonly, fieldControl.sx, onChangeFlushSync, onKeyUp, props.autoCommit, saveChanges]);


    if (props.allowDate && props.allowTime) {
        return (
            <DateTimePicker<DateUtilsTypes.Instance>
                disabled={fieldControl.disabled}
                closeOnSelect={false}
                onClose={() => saveChanges()}
                value={value}
                onChange={onChange}
                slotProps={{
                    textField: textFieldProps,
                }}
            />
        )
    }
    if (props.allowDate) {
        return (
            <DatePicker<DateUtilsTypes.Instance>
                label={'Date Value'}
                closeOnSelect={false}
                onClose={() => saveChanges()}
                disabled={fieldControl.disabled}
                value={value}
                onChange={onChange}
                slotProps={{
                    textField: textFieldProps,
                }}
            />
        )
    }
    if (props.allowTime) {
        return (
            <TimePicker<DateUtilsTypes.Instance>
                label={'Time Value'}
                closeOnSelect={false}
                onClose={() => saveChanges()}
                disabled={fieldControl.disabled}
                value={value}
                onChange={onChange}
                slotProps={{
                    textField: textFieldProps,
                }}
            />
        )
    }

    throw new Error("At least one of [allowDate] and [allowTime] must be true for the date input to work.");
};


export default FieldDateTimePicker;
